import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { toast } from "sonner";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("contact-submit", {
        body: { name, email, message },
      });
      if (error || data?.error) throw new Error(data?.error ?? error?.message);
      toast.success("Message sent! We'll be in touch within 48 hours.");
      setName("");
      setEmail("");
      setMessage("");
    } catch {
      toast.error(
        "Something went wrong sending your message. Please email info@marquettabryant.com directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Contact Marquetta Bryant | Book a Strategic Audit"
        description="Reach Marquetta Bryant, licensed real estate broker and Mortgage Advisor (NMLS #2512081), for speaking, partnership, or client inquiries."
        canonical="https://marquettabryant.com/contact"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Contact",path:"/contact"}])]}
      />
      <Header />
      <main id="main-content" className="pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">Get in Touch</h1>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Whether it's a speaking inquiry, partnership, or general question — we'd love to hear from you.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <p className="text-muted-foreground">info@marquettabryant.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Phone</h3>
                    <p className="text-muted-foreground">Available upon request</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Location</h3>
                    <p className="text-muted-foreground">Serving clients nationwide</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-5 bg-muted/30 p-8 rounded-2xl border border-border">
                <div>
                  <label htmlFor="contact-name" className="sr-only">Your name</label>
                  <Input
                    id="contact-name"
                    name="name"
                    placeholder="Your Name"
                    aria-label="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="sr-only">Your email</label>
                  <Input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="Your Email"
                    aria-label="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="sr-only">Your message</label>
                  <Textarea
                    id="contact-message"
                    name="message"
                    placeholder="Your Message"
                    aria-label="Your message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={5}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent text-secondary hover:bg-accent/90 font-semibold h-12 text-base"
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
